import { Request } from '../request';
import {
    TGetPaymentBillingFreeCreditList,
    TGetPaymentBillingLeadgenFeeList,
    TGetPaymentBillingProListingFeeList,
    TGetCredentialingDepositListParams,
    InvoiceListParams,
} from 'types/financials';

// Listing billing banner. provider/org both resolved from schedule-token; no params.
export const getListingBillingBanner = () =>
    Request.get({
        url: '/schedule-server/provider/billing/listing-banner',
        disableErrorNote: true,
    });

export const getInsuranceForecastSummary = (timeRangeMonths: number) =>
    Request.get({
        url: '/schedule-server/provider/financials/insurance/forecast/summary',
        params: { timeRangeMonths },
        disableErrorNote: true,
    });

// ---- Group practice financials (Practice View). Same shapes as the provider endpoints above. ----
export const getPracticeInsuranceForecastSummary = (timeRangeMonths: number) =>
    Request.get({
        url: '/schedule-server/provider/financials/insurance/forecast/practice/summary',
        params: { timeRangeMonths },
        disableErrorNote: true,
    });

export const getPracticeLeadGenTotalInvestment = (timeRangeMonths: number) =>
    Request.get({
        url: '/schedule-server/provider/billing/practice/lead-gen/total-investment',
        params: { timeRangeMonths },
        disableErrorNote: true,
    });

export const getPracticeProListingFeeSummary = (timeRangeMonths: number) =>
    Request.get({
        url: '/schedule-server/provider/billing/practice/pro-listing-fee/summary',
        params: { timeRangeMonths },
        disableErrorNote: true,
    });

export const getPaymentBillingPracticeLeadgenFeeList = (param: Partial<TGetPaymentBillingLeadgenFeeList>) =>
    Request.post({
        url: '/schedule-server/provider/billing/practice/lead-gen/list',
        params: param,
    });

export const getPaymentBillingPracticeProListingFeeList = (param: Partial<TGetPaymentBillingProListingFeeList>) =>
    Request.post({
        url: '/schedule-server/provider/billing/practice/pro-listing-fee/list',
        params: param,
    });

export const getPaymentBillingPracticeFreeCreditList = (param: Partial<TGetPaymentBillingFreeCreditList>) =>
    Request.post({
        url: '/schedule-server/provider/credits/practice/list',
        params: param,
    });

export const getTotalPayoutsReceived = (timeRangeMonths: number) =>
    Request.get({
        url: '/schedule-server/provider/billing/total-payouts-received',
        params: { timeRangeMonths },
        disableErrorNote: true,
    });

export const getLeadGenTotalInvestment = (timeRangeMonths: number) =>
    Request.get({
        url: '/schedule-server/provider/billing/lead-gen/total-investment',
        params: { timeRangeMonths },
        disableErrorNote: true,
    });

export const getProListingFeeSummary = (timeRangeMonths: number) =>
    Request.get({
        url: '/schedule-server/provider/billing/pro-listing-fee/summary',
        params: { timeRangeMonths },
        disableErrorNote: true,
    });

export const getPaymentBillingLeadgenFeeList = (param: Partial<TGetPaymentBillingLeadgenFeeList>) =>
    Request.post({
        url: '/schedule-server/provider/billing/lead-gen/list',
        params: param,
    });

export const getPaymentBillingProListingFeeList = (param: Partial<TGetPaymentBillingProListingFeeList>) =>
    Request.post({
        url: '/schedule-server/provider/billing/pro-listing-fee/list',
        params: param,
    });

export const getPaymentBillingFreeCreditList = (param: Partial<TGetPaymentBillingFreeCreditList>) =>
    Request.post({
        url: '/schedule-server/provider/credits/list',
        params: param,
    });

export const kanmonPaymentActivate = (paymentActiveStatus: 'A' | 'D') =>
    Request.post({
        url: '/schedule-server/provider/financials/kanmon/payment/activate',
        params: {
            paymentActiveStatus,
        },
    });

export const getKanmonProducts = () =>
    Request.get({
        url: '/schedule-server/provider/financials/kanmon/products/current',
    });

export const selectKanmonProduct = (productId: string, pricePlanId: string) =>
    Request.post({
        url: '/schedule-server/provider/financials/kanmon/price-plan/select',
        params: {
            productId,
            pricePlanId,
        },
    });

export const getInvoiceViewUrl = (billingId: number) =>
    Request.get({
        url: `/schedule-server/provider/stripe/invoice/${billingId}/view-url`,
    });

// ---- Credentialing · Panel deposits (financials) ----
export const getCredentialingDepositSummary = (timeRangeMonths: number) =>
    Request.get({
        url: '/schedule-server/provider/credentialing/deposit/summary',
        params: { periodMonths: timeRangeMonths },
        disableErrorNote: true,
    });

export const getPracticeCredentialingDepositSummary = (timeRangeMonths: number) =>
    Request.get({
        url: '/schedule-server/provider/credentialing/deposit/practice/summary',
        params: { periodMonths: timeRangeMonths },
        disableErrorNote: true,
    });

export const getCredentialingDepositList = (param: Partial<TGetCredentialingDepositListParams>) =>
    Request.get({
        url: '/schedule-server/provider/credentialing/deposit/list',
        params: param,
    });

export const getPracticeCredentialingDepositList = (param: Partial<TGetCredentialingDepositListParams>) =>
    Request.get({
        url: '/schedule-server/provider/credentialing/deposit/practice/list',
        params: param,
    });

// ---------------- Insurance Billing Invoices（Financials → Insurance 的月度 managed billing fee 账单）----------------
const INSURANCE_INVOICES_BASE = '/schedule-server/provider/insurance-billing/invoices';

// 列表 / summary 出错时按约定直接展示后端 message（已按用户可读措辞写好）→ 走默认错误 toast
export const getInsuranceInvoices = (params: InvoiceListParams) =>
    Request.get({ url: INSURANCE_INVOICES_BASE, params });

// summary 只认 months/keyword/status，须与列表当前筛选同参
export const getInsuranceInvoicesSummary = (params: Pick<InvoiceListParams, 'months' | 'keyword' | 'status'>) =>
    Request.get({ url: `${INSURANCE_INVOICES_BASE}/summary`, params });

// 明细 / PDF 出错时前端按场景固定文案（91023 的后端 message 对这两个场景有误导），不透传
export const getInsuranceInvoiceDetail = (invoiceId: number) =>
    Request.get({ url: `${INSURANCE_INVOICES_BASE}/${invoiceId}`, disableErrorNote: true });

// S3 预签名短链 900 秒过期，每次点击现取，不要缓存
export const getInsuranceInvoicePdfLink = (invoiceId: number) =>
    Request.get({ url: `${INSURANCE_INVOICES_BASE}/${invoiceId}/pdf`, disableErrorNote: true });
